import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  RotateCw,
  Monitor,
  Tablet,
  Smartphone,
  Sparkles,
  CheckCircle,
  Globe,
  Loader2,
  Image as ImageIcon
} from "lucide-react";
import { useLang } from "../i18n.jsx";

export default function TemplateSneakPeakModal({ site, isOpen, onClose }) {
  const { lang } = useLang();
  const [device, setDevice] = useState("desktop"); // 'desktop' | 'tablet' | 'mobile'
  const [isLoading, setIsLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [showScreenshot, setShowScreenshot] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setShowScreenshot(false);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, site, reloadKey]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!site) return null;

  const handleSelectTemplate = () => {
    onClose();
    // Dispatch custom event for plan / template selection in LeadForm
    const templateTitle = `Template: ${site.name} (${site.category})`;
    window.dispatchEvent(new CustomEvent("ap:select-plan", { detail: templateTitle }));
    const formEl = document.getElementById("konsultasi");
    if (formEl) {
      formEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  const getFrameWidth = () => {
    switch (device) {
      case "mobile":
        return "w-[390px] max-w-full";
      case "tablet":
        return "w-[768px] max-w-full";
      default:
        return "w-full";
    }
  };

  const isId = lang !== "en";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/85 backdrop-blur-md"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex flex-col w-full max-w-6xl h-[92vh] rounded-3xl border border-paper/20 bg-ink shadow-2xl shadow-black/80 overflow-hidden"
          >
            {/* Top Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-paper/10 bg-ink2/80 px-4 sm:px-6 py-3.5">
              {/* Site Info */}
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-3.5 h-3.5 rounded-full shrink-0"
                  style={{ backgroundColor: site.hue || "#D4FF00" }}
                />
                <div className="truncate">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm sm:text-base font-bold text-paper truncate font-display">
                      {site.name}
                    </h3>
                    <span className="hidden sm:inline-block rounded-full bg-paper/10 px-2.5 py-0.5 text-[10px] font-semibold text-acid uppercase tracking-wider">
                      {site.category}
                    </span>
                  </div>
                  <p className="flex items-center gap-1 text-[11px] text-paper/50 truncate">
                    <Globe className="w-3 h-3 shrink-0" />
                    <span>{site.domain}</span>
                  </p>
                </div>
              </div>

              {/* Viewport Switcher Controls */}
              <div className="flex items-center gap-1 rounded-full border border-paper/15 bg-black/40 p-1">
                <button
                  type="button"
                  onClick={() => setDevice("desktop")}
                  title={isId ? "Tampilan Desktop" : "Desktop View"}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1 text-xs font-medium transition ${
                    device === "desktop"
                      ? "bg-paper/20 text-paper shadow-sm"
                      : "text-paper/60 hover:text-paper hover:bg-paper/10"
                  }`}
                >
                  <Monitor className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Desktop</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDevice("tablet")}
                  title={isId ? "Tampilan Tablet (768px)" : "Tablet View (768px)"}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1 text-xs font-medium transition ${
                    device === "tablet"
                      ? "bg-paper/20 text-paper shadow-sm"
                      : "text-paper/60 hover:text-paper hover:bg-paper/10"
                  }`}
                >
                  <Tablet className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Tablet</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDevice("mobile")}
                  title={isId ? "Tampilan Mobile (390px)" : "Mobile View (390px)"}
                  className={`flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1 text-xs font-medium transition ${
                    device === "mobile"
                      ? "bg-paper/20 text-paper shadow-sm"
                      : "text-paper/60 hover:text-paper hover:bg-paper/10"
                  }`}
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Mobile</span>
                </button>
              </div>

              {/* Action Buttons & Close */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowScreenshot((prev) => !prev)}
                  title={
                    showScreenshot
                      ? (isId ? "Tampilkan Live Embed Iframe" : "Show Live Embed Iframe")
                      : (isId ? "Tampilkan Screenshot Statis" : "Show Static Screenshot")
                  }
                  className={`flex items-center gap-1 rounded-full border border-paper/15 px-2.5 py-1.5 text-xs transition ${
                    showScreenshot
                      ? "border-acid text-acid bg-acid/10"
                      : "text-paper/70 hover:text-paper hover:border-paper/30"
                  }`}
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  <span className="hidden lg:inline">
                    {showScreenshot ? (isId ? "Mode Live" : "Live Mode") : "Screenshot"}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setReloadKey((k) => k + 1)}
                  title={isId ? "Muat Ulang Halaman" : "Reload Preview"}
                  className="rounded-full border border-paper/15 p-1.5 text-paper/70 transition hover:text-paper hover:border-paper/30"
                >
                  <RotateCw className="w-4 h-4" />
                </button>

                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={isId ? "Buka di Tab Baru" : "Open in New Tab"}
                  className="rounded-full border border-paper/15 p-1.5 text-paper/70 transition hover:text-acid hover:border-acid"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>

                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Tutup"
                  className="rounded-full bg-paper/10 p-1.5 text-paper/70 transition hover:bg-paper/20 hover:text-paper"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Preview Frame Area */}
            <div className="relative flex-1 w-full bg-black/60 overflow-hidden flex items-center justify-center p-2 sm:p-4">
              <div
                className={`relative h-full ${getFrameWidth()} transition-all duration-300 flex flex-col rounded-2xl overflow-hidden border border-paper/20 bg-ink shadow-2xl`}
              >
                {/* Simulated Browser Bar */}
                <div className="flex items-center justify-between border-b border-paper/10 bg-ink2/90 px-3 py-1.5 select-none">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <div className="flex items-center gap-1.5 rounded-md bg-black/40 px-3 py-0.5 text-[11px] text-paper/50 font-mono max-w-[60%] truncate">
                    <span className="text-acid/80">https://</span>
                    <span className="truncate">{site.domain}</span>
                  </div>
                  <div className="w-10" />
                </div>

                {/* Content: Iframe or Screenshot */}
                <div className="relative flex-1 w-full h-full bg-[#0D0D0D] overflow-hidden">
                  {/* Loading Spinner */}
                  {isLoading && !showScreenshot && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-ink/90 backdrop-blur-sm text-paper">
                      <Loader2 className="w-8 h-8 animate-spin text-acid" />
                      <p className="text-xs text-paper/70 font-medium">
                        {isId ? "Memuat live preview..." : "Loading live preview..."}
                      </p>
                    </div>
                  )}

                  {showScreenshot ? (
                    <div className="relative w-full h-full overflow-y-auto">
                      <img
                        src={site.img}
                        alt={site.name}
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  ) : (
                    <iframe
                      key={`${site.url}-${reloadKey}-${device}`}
                      src={site.url}
                      title={`Sneak Peak ${site.name}`}
                      className="w-full h-full border-0 bg-transparent"
                      onLoad={() => setIsLoading(false)}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Bar / CTA */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-paper/10 bg-ink2/90 px-4 sm:px-6 py-3">
              <div className="text-xs text-paper/70 max-w-xl truncate">
                <span className="font-semibold text-paper mr-1.5">{site.name}:</span>
                <span>{site.blurb}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-full border border-paper/20 px-4 py-2 text-xs font-semibold text-paper transition hover:border-acid hover:text-acid"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>{isId ? "Buka Situs Lengkap" : "Open Full Site"}</span>
                </a>
                <button
                  type="button"
                  onClick={handleSelectTemplate}
                  className="flex items-center gap-1.5 rounded-full bg-acid px-4 py-2 text-xs font-bold text-ink transition hover:bg-paper hover:scale-[1.02] active:scale-95 shadow-lg shadow-acid/20"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{isId ? "Pilih / Konsultasikan Template Ini" : "Use / Inquire This Template"}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
